import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { formatAmount, getFormatConfig, initializeFormats } from '../../../utils/global';
import { useApHub } from '../ApHub/ApHubContext';
import { getDashboardDateRange } from '../ApHub/apHubStorage';
import ApHubBreadcrumb from '../ApHub/ApHubBreadcrumb';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonatePlainIconButtonSx,
  harmonatePageSearchFieldSx,
  harmonatePageSearchIconSx,
} from '../../../theme/harmonateStyles';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';

const getBaseUrl = () => process.env.REACT_APP_BACKEND_URL || '';
const getTenantId = () => localStorage.getItem('tenant_id') || '';
const getUserId = () => localStorage.getItem('user_id') || null;
const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${getBaseUrl()}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      message = body.error || body.message || message;
    } catch { /* ignore */ }
    throw new Error(message);
  }
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

const inDateRange = (value, from, to) => {
  if (!value) return true;
  if (from && value < from) return false;
  if (to && value > to) return false;
  return true;
};

const mapPropertiesToBusinessUnits = (properties) =>
  (Array.isArray(properties) ? properties : [])
    .filter((p) => p.businessUnitId)
    .map((p) => ({
      businessUnitId: p.businessUnitId,
      businessUnitName: p.propertyName,
      businessUnitCode: p.propertyCode,
      propertyId: p.propertyId,
      parentBusinessUnitId: null,
      status: p.status || 'ACTIVE',
    }));

const fetchPropertyBusinessUnits = async (tenantId, { scope = 'user' } = {}) => {
  if (!tenantId) return [];

  const properties = await apiFetch(`conductor/cash-recon/properties/by-tenant?tenant_id=${tenantId}`);
  const list = Array.isArray(properties) ? properties : [];

  if (scope === 'tenant') {
    return mapPropertiesToBusinessUnits(list);
  }

  const userId = getUserId();
  if (!userId) {
    return mapPropertiesToBusinessUnits(list);
  }

  const userProps = await apiFetch(
    `conductor/user-tenant-properties?tenant_id=${tenantId}&user_id=${userId}`
  );
  const allowedPropertyIds = new Set(
    (Array.isArray(userProps) ? userProps : []).map((p) => p.propertyId)
  );

  return mapPropertiesToBusinessUnits(
    list.filter((p) => p.propertyId && allowedPropertyIds.has(p.propertyId))
  );
};

const fetchExpenseLookupMaps = async (tenantId) => {
  const [businessUnits, glAccounts, budgetCodes] = await Promise.all([
    fetchPropertyBusinessUnits(tenantId, { scope: 'user' }),
    apiFetch(`conductor/cash-recon/chart-of-account/by-tenant?tenant_id=${tenantId}`).catch(() => []),
    apiFetch(`conductor/budget-codes/tenant/${tenantId}`).catch(() => []),
  ]);

  return {
    businessUnits: Object.fromEntries(
      businessUnits.map((u) => [u.businessUnitId, u.businessUnitName])
    ),
    glAccounts: Object.fromEntries(
      (Array.isArray(glAccounts) ? glAccounts : []).map((g) => [
        g.ledgerAccountId,
        `${g.glCode || ''}${g.glDescription ? ` - ${g.glDescription}` : ''}`.trim(),
      ])
    ),
    budgetCodes: Object.fromEntries(
      (Array.isArray(budgetCodes) ? budgetCodes : []).map((b) => [b.budgetCodeId, b.code])
    ),
  };
};

const formatDateRangeLabel = (range) => {
  if (!range.from && !range.to) return 'All dates';
  if (range.from && range.to) return `${range.from} to ${range.to}`;
  if (range.from) return `From ${range.from}`;
  return `Through ${range.to}`;
};

const buildGlAccountMaps = (accounts = []) => {
  const labelMap = {};
  const codeMap = {};
  const descriptionMap = {};

  accounts.forEach((account) => {
    const id = account.ledgerAccountId;
    const code = account.glCode || '';
    const description = account.glDescription || '';
    labelMap[id] = `${code}${description ? ` - ${description}` : ''}`.trim() || '—';
    codeMap[id] = code;
    descriptionMap[id] = description;
  });

  return { labelMap, codeMap, descriptionMap };
};

const fetchVendorNameMap = async (tenantId) => {
  const vendors = await apiFetch(`conductor/vendor-masters/tenant/${tenantId}`).catch(() => []);
  return Object.fromEntries(
    (Array.isArray(vendors) ? vendors : []).map((v) => [v.vendorId, v.vendorName])
  );
};

const fetchGlAssignmentRows = async (tenantId, businessUnitId, dateRange = {}) => {
  if (!tenantId || !businessUnitId) return [];

  const { from = '', to = '' } = dateRange;

  const [lines, headers, lookups, vendors, glAccounts] = await Promise.all([
    apiFetch(
      `conductor/ap-invoice-lines/by-business-unit?tenant_id=${tenantId}&business_unit_id=${businessUnitId}`
    ).catch(() => []),
    apiFetch(
      `conductor/ap-invoice-headers/by-business-unit?tenant_id=${tenantId}&business_unit_id=${businessUnitId}`
    ).catch(() => []),
    fetchExpenseLookupMaps(tenantId),
    fetchVendorNameMap(tenantId),
    apiFetch(`conductor/cash-recon/chart-of-account/by-tenant?tenant_id=${tenantId}`).catch(() => []),
  ]);

  const lineList = Array.isArray(lines) ? lines : [];
  const headerList = Array.isArray(headers) ? headers : [];
  const { labelMap, codeMap, descriptionMap } = buildGlAccountMaps(
    Array.isArray(glAccounts) ? glAccounts : []
  );

  const invoiceMap = Object.fromEntries(
    headerList.map((header) => [header.apInvoiceId, header])
  );

  const allowedInvoiceIds = new Set(
    headerList
      .filter((header) => inDateRange(header.invoiceDate, from, to))
      .map((header) => header.apInvoiceId)
  );

  return lineList
    .filter((line) => allowedInvoiceIds.has(line.apInvoiceId))
    .map((line, index) => {
      const invoice = invoiceMap[line.apInvoiceId] || {};
      const glAccountId = line.glAccountId || '';
      return {
        id: line.apInvoiceLineId || `line-${index}`,
        apInvoiceLineId: line.apInvoiceLineId,
        apInvoiceId: line.apInvoiceId,
        invoiceNumber: line.invoiceNumber || invoice.invoiceNumber || '',
        invoiceDate: invoice.invoiceDate || '',
        vendorName: invoice.vendorName || vendors[invoice.vendorId] || '',
        lineDescription: line.lineItemDescription || '',
        glAccountId,
        glCode: codeMap[glAccountId] || lookups.glAccounts?.[glAccountId]?.split(' - ')[0] || '',
        glDescription: descriptionMap[glAccountId] || '',
        glAccountLabel: labelMap[glAccountId] || lookups.glAccounts?.[glAccountId] || '—',
        budgetCode: lookups.budgetCodes?.[line.budgetCodeId] || '',
        amount: Number(line.lineItemTotal) || 0,
      };
    })
    .sort((a, b) => {
      const dateCompare = (b.invoiceDate || '').localeCompare(a.invoiceDate || '');
      if (dateCompare !== 0) return dateCompare;
      return (a.invoiceNumber || '').localeCompare(b.invoiceNumber || '');
    });
};

const groupGlAssignmentRows = (rows = []) => {
  const groups = new Map();

  rows.forEach((row) => {
    const key = row.glAccountId || '__unassigned__';
    if (!groups.has(key)) {
      groups.set(key, {
        id: key,
        glAccountId: row.glAccountId || '',
        glCode: row.glCode || '—',
        glDescription: row.glDescription || '',
        glAccountLabel: row.glAccountLabel || '—',
        lineCount: 0,
        invoiceIds: new Set(),
        totalAmount: 0,
      });
    }

    const group = groups.get(key);
    group.lineCount += 1;
    if (row.apInvoiceId) group.invoiceIds.add(row.apInvoiceId);
    group.totalAmount += row.amount || 0;
  });

  return [...groups.values()]
    .filter((group) => group.glAccountId)
    .map((group) => ({
      id: group.id,
      glAccountId: group.glAccountId,
      glCode: group.glCode,
      glDescription: group.glDescription,
      glAccountLabel: group.glAccountLabel,
      lineCount: group.lineCount,
      invoiceCount: group.invoiceIds.size,
      totalAmount: group.totalAmount,
    }))
    .sort((a, b) => (a.glCode || '').localeCompare(b.glCode || ''));
};

const GlAssignments = () => {
  const tenantId = getTenantId();
  const config = getFormatConfig();
  const location = useLocation();
  const { selectedBusinessUnitId } = useApHub();
  const dateRange = useMemo(() => getDashboardDateRange(), [location.pathname]);
  const hasRequiredFilters = Boolean(
    selectedBusinessUnitId && dateRange.from && dateRange.to
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lineRows, setLineRows] = useState([]);
  const [search, setSearch] = useState('');

  const loadRows = useCallback(async (options = {}) => {
    const { silent = false } = options;
    if (!tenantId || !hasRequiredFilters) {
      setLineRows([]);
      if (!silent) setLoading(false);
      return;
    }

    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      const data = await fetchGlAssignmentRows(tenantId, selectedBusinessUnitId, dateRange);
      setLineRows(data);
    } catch (err) {
      if (!silent) setError(err.message || 'Failed to load GL assignments');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [tenantId, hasRequiredFilters, selectedBusinessUnitId, dateRange]);

  useEffect(() => {
    if (tenantId) {
      initializeFormats(tenantId);
    }
    loadRows();
  }, [tenantId, loadRows]);

  const groupedRows = useMemo(
    () => groupGlAssignmentRows(lineRows),
    [lineRows]
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groupedRows;
    return groupedRows.filter((row) => (
      (row.glCode || '').toLowerCase().includes(q)
      || (row.glDescription || '').toLowerCase().includes(q)
      || (row.glAccountLabel || '').toLowerCase().includes(q)
    ));
  }, [groupedRows, search]);

  const columns = useMemo(() => [
    {
      field: 'glCode',
      headerName: 'GL Code',
      flex: 0.9,
      minWidth: 120,
      valueFormatter: (params) => params.value || '—',
    },
    {
      field: 'glDescription',
      headerName: 'GL Description',
      flex: 1.4,
      minWidth: 180,
      valueFormatter: (params) => params.value || '—',
    },
    {
      field: 'invoiceCount',
      headerName: 'Invoices',
      flex: 0.7,
      minWidth: 100,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'lineCount',
      headerName: 'Lines',
      flex: 0.7,
      minWidth: 90,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'totalAmount',
      headerName: 'Total Amount',
      flex: 1,
      minWidth: 140,
      align: 'left',
      headerAlign: 'left',
      valueFormatter: (params) => formatAmount(params.value, config.AmountFormat),
    },
  ], [config.AmountFormat]);

  if (!hasRequiredFilters) {
    const missingBusinessUnit = !selectedBusinessUnitId;
    const missingDateRange = !dateRange.from || !dateRange.to;
    let message = 'Select a business unit and date range on the dashboard to view GL codes.';
    if (missingBusinessUnit && !missingDateRange) {
      message = 'Select a business unit on the dashboard to view GL codes.';
    } else if (!missingBusinessUnit && missingDateRange) {
      message = 'Select a date range on the dashboard to view GL codes.';
    }

    return (
      <Box sx={{ ...harmonatePageSx, height: '100%' }}>
        <ApHubBreadcrumb variant="harmonate" segments={[{ label: 'GL Codes' }]} />
        <Alert severity="info">{message}</Alert>
      </Box>
    );
  }

  if (loading && lineRows.length === 0) {
    return (
      <Box sx={{ ...harmonatePageSx, justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress sx={{ color: 'white' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ ...harmonatePageSx, flex: 1, minHeight: 0, height: '100%' }}>
      <ApHubBreadcrumb variant="harmonate" segments={[{ label: 'GL Codes' }]} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h6" sx={harmonatePageTitleSx}>
            GL Codes
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: ht.white }}>
            {formatDateRangeLabel(dateRange)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search GL code or description"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            variant="outlined"
            sx={{ ...harmonatePageSearchFieldSx, width: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={harmonatePageSearchIconSx} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')} edge="end" sx={{ color: ht.white }}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <IconButton
            onClick={() => loadRows()}
            aria-label="Refresh GL codes"
            sx={harmonatePlainIconButtonSx}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper elevation={0} sx={{ ...harmonateCardSx, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <DataGridPro
          rows={filteredRows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          autoHeight={false}
          rowHeight={40}
          columnResizeMode="onEnd"
          pagination
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          sx={{
            ...harmonateDataGridSx,
            flex: 1,
            minHeight: 0,
            height: '100%',
          }}
        />
      </Paper>
    </Box>
  );
};

export default GlAssignments;
